import { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, CheckCircle, Clock, Mail, MapPin, RefreshCw, ShieldAlert, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function formatDateTime(value) {
  return new Date(value).toLocaleString('pt-BR');
}

export default function EmailApprovalsPage() {
  const navigate = useNavigate();
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [approverEmailsText, setApproverEmailsText] = useState('');
  const [savingApprovers, setSavingApprovers] = useState(false);

  useEffect(() => {
    fetchApprovals();
    fetchApprovers();
  }, []);

  const fetchApprovals = async () => {
    try {
      const response = await axios.get(`${API}/email/approvals`);
      setApprovals(response.data || []);
    } catch (error) {
      toast.error('Erro ao carregar aprovações de email');
    } finally {
      setLoading(false);
    }
  };

  const fetchApprovers = async () => {
    try {
      const response = await axios.get(`${API}/email/approval-approvers`);
      setApproverEmailsText((response.data?.emails || []).join('\n'));
    } catch (error) {
      toast.error('Erro ao carregar emails aprovadores');
    }
  };

  const handleSaveApprovers = async () => {
    setSavingApprovers(true);
    try {
      const emails = approverEmailsText
        .split(/[\n,;]+/)
        .map((item) => item.trim())
        .filter(Boolean);
      const response = await axios.put(`${API}/email/approval-approvers`, { emails });
      setApproverEmailsText((response.data?.emails || []).join('\n'));
      toast.success(response.data?.message || 'Aprovadores atualizados');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar emails aprovadores');
    } finally {
      setSavingApprovers(false);
    }
  };

  const handleApprove = async (approvalId) => {
    setProcessingId(approvalId);
    try {
      const response = await axios.post(`${API}/email/approvals/${approvalId}/approve`);
      toast.success(response.data?.message || 'Solicitação aprovada');
      await fetchApprovals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao aprovar solicitação');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (approvalId) => {
    const reason = window.prompt('Informe o motivo da rejeição:', 'A reunião não foi aprovada pelo administrador.');
    if (reason === null) {
      return;
    }

    setProcessingId(approvalId);
    try {
      const response = await axios.post(`${API}/email/approvals/${approvalId}/reject`, {
        reason
      });
      toast.success(response.data?.message || 'Solicitação rejeitada');
      await fetchApprovals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao rejeitar solicitação');
    } finally {
      setProcessingId(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border/40 bg-white/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button onClick={() => navigate('/dashboard')} variant="ghost" size="sm" className="rounded-full">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
                <ShieldAlert className="w-5 h-5 text-primary-foreground" />
              </div>
              <h1 className="text-xl font-manrope font-bold tracking-tight">Aprovações de Email</h1>
            </div>
          </div>
          <Button onClick={fetchApprovals} variant="outline" className="rounded-full">
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-12 py-10">
        <div className="bg-white rounded-2xl border border-border/40 p-6 shadow-sm mb-6">
          <div className="flex items-center justify-between gap-4 mb-4">
            <div>
              <h2 className="text-lg font-manrope font-bold">Emails aprovadores</h2>
              <p className="text-sm text-muted-foreground">
                Cadastre um email por linha. Quando uma solicitação externa entrar em aprovação, todos receberão um link e qualquer um poderá aprovar.
              </p>
            </div>
            <Button onClick={handleSaveApprovers} disabled={savingApprovers} className="rounded-full">
              {savingApprovers ? 'Salvando...' : 'Salvar aprovadores'}
            </Button>
          </div>
          <textarea
            value={approverEmailsText}
            onChange={(e) => setApproverEmailsText(e.target.value)}
            placeholder="aprovador1@tecnoservicebrasil.com.br&#10;aprovador2@tecnoservicebrasil.com.br"
            className="w-full min-h-36 border border-border rounded-md px-3 py-3 bg-white"
          />
        </div>
        {approvals.length === 0 ? (
          <div className="bg-white rounded-2xl border border-border/40 p-10 text-center text-muted-foreground">
            <p>Nenhuma solicitação de aprovação encontrada.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {approvals.map((approval) => (
              <div key={approval.id} className="bg-white rounded-2xl border border-border/40 p-6 shadow-sm">
                <div className="flex items-start justify-between gap-6">
                  <div className="space-y-3 min-w-0">
                    <div className="flex items-center gap-3">
                      <h2 className="text-lg font-manrope font-bold truncate">{approval.meeting_name}</h2>
                      <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                        approval.status === 'pending'
                          ? 'bg-amber-100 text-amber-700'
                          : approval.status === 'approved'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                      }`}>
                        {approval.status === 'pending' ? 'Pendente' : approval.status === 'approved' ? 'Aprovada' : 'Rejeitada'}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-muted-foreground">
                      <p>Solicitante: {approval.organizer_name || '-'}</p>
                      <p className="flex items-center gap-2"><Mail className="w-4 h-4" /> {approval.organizer_email}</p>
                      <p className="flex items-center gap-2"><MapPin className="w-4 h-4" /> {approval.room_name}</p>
                      <p className="flex items-center gap-2"><Clock className="w-4 h-4" /> {formatDateTime(approval.start_time)} - {formatDateTime(approval.end_time)}</p>
                      <p>Origem: {approval.source}</p>
                    </div>
                    {approval.approver_emails?.length > 0 && (
                      <p className="text-sm text-muted-foreground">Aprovadores notificados: {approval.approver_emails.join(', ')}</p>
                    )}
                    {approval.attendees?.length > 0 && (
                      <p className="text-sm text-muted-foreground">Participantes: {approval.attendees.join(', ')}</p>
                    )}
                    {approval.rejection_reason && (
                      <p className="text-sm text-red-600">Motivo da rejeição: {approval.rejection_reason}</p>
                    )}
                  </div>

                  {approval.status === 'pending' && (
                    <div className="flex flex-col gap-3 shrink-0">
                      <Button
                        onClick={() => handleApprove(approval.id)}
                        disabled={processingId === approval.id}
                        className="rounded-full"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Aprovar
                      </Button>
                      <Button
                        onClick={() => handleReject(approval.id)}
                        disabled={processingId === approval.id}
                        variant="outline"
                        className="rounded-full"
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Rejeitar
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
